import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  DocumentIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ReceiptRefundIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';

const InvoicePreview = ({ invoice }) => {
  const navigate = useNavigate();

  if (!invoice) {
    return (
      <div className="p-6 bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20">
        <div className="flex items-center mb-6">
          <button 
            onClick={() => navigate('/invoicing')} 
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors mr-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-bold text-white">Détails de la Facture</h2>
        </div>
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <DocumentTextIcon className="h-16 w-16 text-white/30 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Facture introuvable</h3>
          <p className="text-white/60">Impossible de charger les détails de cette facture.</p>
        </div>
      </div>
    );
  }

  // Préparer les données pour l'affichage
  const amount = parseFloat(invoice.amount.replace(/\s/g, '').replace('DA', '').trim());
  const invoiceData = {
    invoiceNumber: invoice.invoiceNumber || invoice.id,
    issueDate: invoice.date,
    items: [{ description: 'Service', quantity: 1, price: amount, total: amount }],
    subtotal: amount,
    discount: 0,
    tax: 0,
    total: amount
  };
  
  const clientName = invoice.client;
  const clientInfo = {
    firstName: clientName.split(' ')[0] || '',
    lastName: clientName.split(' ').slice(1).join(' ') || '',
    email: 'client@email.dz',
    phone: '+213 XXX XXX XXX',
    memberCode: 'MEM-' + invoice.id
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    try {
      console.log('Téléchargement du PDF démarré');
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  return (
    <div className="p-6 bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20">
      {/* En-tête avec actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/invoicing')} 
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors mr-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-bold text-white">
            Facture {invoice.invoiceNumber}
          </h2>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handlePrint}
            className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-1"
          >
            <PrinterIcon className="h-4 w-4" />
            <span>Imprimer</span>
          </button>
          <button
            onClick={handleDownloadPDF}
            className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-1"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>PDF</span>
          </button>
        </div>
      </div>

      {/* Facture prévisualisée */}
      <div className="bg-white text-gray-800 rounded-xl overflow-hidden shadow-2xl print:shadow-none">
        {/* En-tête de la facture */}
        <div className="bg-indigo-900 text-white p-6">
          <div className="flex justify-between">
            <div>
              <h2 className="text-3xl font-bold">FACTURE</h2>
              <p className="mt-1 opacity-70">SYSTÈME DE GESTION DE VOYAGE</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{invoice.invoiceNumber}</div>
              <div className="mt-1 opacity-70">Date: {invoice.date}</div>
            </div>
          </div>
        </div>
        
        {/* Informations client et entreprise */}
        <div className="grid grid-cols-2 gap-10 p-6 border-b">
          <div>
            <h3 className="font-bold text-gray-600 mb-2">Facturé à:</h3>
            <div className="text-lg font-bold">{invoice.client}</div>
            <div className="text-gray-600 mt-1">client@email.dz</div>
            <div className="text-gray-600">+213 XXX XXX XXX</div>
          </div>
          <div>
            <h3 className="font-bold text-gray-600 mb-2">De:</h3>
            <div className="text-lg font-bold">TravelIT</div>
            <div className="text-gray-600 mt-1">Système de Gestion de Voyage</div>
            <div className="text-gray-600">contact@travelit.dz</div>
            <div className="text-gray-600">+213 XX XX XX XX</div>
          </div>
        </div>

        {/* Articles */}
        <div className="p-6">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="border border-gray-200 p-3 text-left">Description</th>
                <th className="border border-gray-200 p-3 text-center">Quantité</th>
                <th className="border border-gray-200 p-3 text-right">Prix</th>
                <th className="border border-gray-200 p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-200 p-3">Service</td>
                <td className="border border-gray-200 p-3 text-center">1</td>
                <td className="border border-gray-200 p-3 text-right">{invoice.amount}</td>
                <td className="border border-gray-200 p-3 text-right">{invoice.amount}</td>
              </tr>
            </tbody>
          </table>
          
          {/* Totaux */}
          <div className="flex justify-end mt-6">
            <div className="w-1/3">
              <div className="flex justify-between border-b pb-2">
                <span className="font-semibold">Sous-total:</span>
                <span>{invoice.amount}</span>
              </div>
              <div className="flex justify-between pt-2 font-bold text-lg">
                <span>Total:</span>
                <span>{invoice.amount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Pied de page */}
        <div className="bg-gray-100 p-6 text-center text-gray-600">
          <p>Merci pour votre confiance!</p>
          <p className="text-sm mt-2">Pour toute question concernant cette facture, veuillez nous contacter.</p>
        </div>
      </div>

      {/* Statut de la facture */}
      <div className="mt-6 p-4 bg-white/5 backdrop-blur-lg rounded-xl border border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium">Statut:</h3>
            <div className="mt-1">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                invoice.status === 'paid' 
                  ? 'bg-green-500/20 text-green-400' 
                  : invoice.status === 'pending'
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {invoice.status === 'paid' ? 'Payée' : 
                 invoice.status === 'pending' ? 'En attente' : 'En retard'}
              </span>
            </div>
          </div>
          <div>
            <h3 className="text-white font-medium">Type:</h3>
            <div className="mt-1">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                invoice.type === 'client' 
                  ? 'bg-blue-500/20 text-blue-400' 
                  : invoice.type === 'supplier'
                  ? 'bg-purple-500/20 text-purple-400'
                  : 'bg-green-500/20 text-green-400'
              }`}>
                {invoice.type === 'client' ? 'Client' : 
                 invoice.type === 'supplier' ? 'Fournisseur' : 'Service'}
              </span>
            </div>
          </div>
          <Link
            to={`/invoicing/${invoice.id}/edit`}
            className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center space-x-1"
          >
            <PencilIcon className="h-4 w-4" />
            <span>Modifier</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

const InvoicingPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [invoicesPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Gérer la visualisation
  const handleView = (invoice) => {
    console.log('Voir détails clicked for', invoice.invoiceNumber);
    navigate(`/invoicing/${invoice.id}`);
  };

  // Gérer la modification
  const handleEdit = (invoice) => {
    console.log('Edit clicked for', invoice.invoiceNumber);
    navigate(`/invoicing/${invoice.id}/edit`);
  };

  // Utilisation des données réelles de facturation (à implémenter avec API)
  const mockInvoices = [];

  const allInvoices = mockInvoices;
  
  const filteredInvoices = allInvoices.filter(invoice => {
    if (activeTab === 'all') return true;
    if (activeTab === 'paid') return invoice.status === 'paid';
    if (activeTab === 'pending') return invoice.status === 'pending';
    if (activeTab === 'late') return invoice.status === 'late';
    if (activeTab === 'client') return invoice.type === 'client';
    if (activeTab === 'supplier') return invoice.type === 'supplier';
    if (activeTab === 'service') return invoice.type === 'service';
    return true;
  });
  
  const invoices = filteredInvoices.slice((currentPage - 1) * invoicesPerPage, currentPage * invoicesPerPage);
  const totalInvoices = filteredInvoices.length;
  const totalPages = Math.ceil(totalInvoices / invoicesPerPage);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleDelete = async (invoiceId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) {
      try {
        // Logique de suppression simulée
        console.log(`Suppression de la facture ${invoiceId}`);
      } catch (error) {
        console.error(error);
      }
    }
  };

  // Statistiques des factures
  const stats = [
    {
      title: 'Total Facturé',
      value: '238 500 DA',
      icon: BanknotesIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Montant Payé',
      value: '45 700 DA',
      icon: CheckCircleIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'En Attente',
      value: '168 550 DA',
      icon: ClockIcon,
      color: 'from-amber-500 to-amber-600',
    },
  ];

  // Répartition par type de facture
  const invoiceDistribution = [
    { name: 'Clients', count: 5, color: 'from-blue-400 to-blue-500' },
    { name: 'Fournisseurs', count: 3, color: 'from-purple-400 to-purple-500' },
    { name: 'Services', count: 2, color: 'from-green-400 to-green-500' },
  ];

  // Onglets
  const tabs = [
    { id: 'all', name: 'Toutes les Factures', icon: DocumentTextIcon },
    { id: 'paid', name: 'Payées', icon: CheckCircleIcon },
    { id: 'pending', name: 'En Attente', icon: ClockIcon },
    { id: 'late', name: 'En Retard', icon: ExclamationTriangleIcon },
    { id: 'client', name: 'Clients', icon: ReceiptRefundIcon },
    { id: 'supplier', name: 'Fournisseurs', icon: CurrencyDollarIcon },
  ];

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Facturation
              </h1>
              <p className="text-white/60 mt-2">
                Gestion des factures clients et fournisseurs
              </p>
            </div>
            <Link
              to="/invoicing/new"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nouvelle Facture</span>
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                    <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setCurrentPage(1);
                  }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Répartition par type de facture */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <h3 className="text-xl font-semibold text-white mb-4">
              Répartition par Type de Facture
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {invoiceDistribution.map((item) => (
                <div 
                  key={item.name}
                  className="bg-white/5 rounded-xl p-4 border border-white/10"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-white">{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 bg-gradient-to-r ${item.color} text-white`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full">
                    <div 
                      className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                      style={{ width: `${(item.count / allInvoices.length * 100).toFixed(1)}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-sm text-white/60">
                    {(item.count / allInvoices.length * 100).toFixed(1)}% des factures
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Search and Filters */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Rechercher une facture..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-white/50" />
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="paid">Payées</option>
                  <option value="pending">En attente</option>
                  <option value="late">En retard</option>
                </select>
              </div>
            </div>
          </div>

          {/* Invoices List */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
            {invoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <DocumentTextIcon className="h-16 w-16 text-white/30 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Aucune facture trouvée</h3>
                <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                <Link
                  to="/invoicing/new"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Créer une facture</span>
                </Link>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-4 text-left text-white font-semibold">N° Facture</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Client/Fournisseur</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Date</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Montant</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Type</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {invoices.map((invoice) => (
                        <motion.tr
                          key={invoice.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-white/5 transition-colors"
                        >
                          <td className="px-6 py-4 text-white font-mono text-sm">{invoice.invoiceNumber}</td>
                          <td className="px-6 py-4 text-white font-medium">{invoice.client}</td>
                          <td className="px-6 py-4 text-white/80">{invoice.date}</td>
                          <td className="px-6 py-4 text-white font-medium">{invoice.amount}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              invoice.status === 'paid' 
                                ? 'bg-green-500/20 text-green-400' 
                                : invoice.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {invoice.status === 'paid' ? 'Payée' : 
                               invoice.status === 'pending' ? 'En attente' : 'En retard'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              invoice.type === 'client' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : invoice.type === 'supplier'
                                ? 'bg-purple-500/20 text-purple-400'
                                : 'bg-green-500/20 text-green-400'
                            }`}>
                              {invoice.type === 'client' ? 'Client' : 
                               invoice.type === 'supplier' ? 'Fournisseur' : 'Service'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleView(invoice)}
                                className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                title="Voir les détails"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(invoice)}
                                className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                                title="Modifier"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(invoice.id)}
                                className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                title="Supprimer"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                    <div className="text-sm text-white/60">
                      Affichage de {(currentPage - 1) * invoicesPerPage + 1} à {Math.min(currentPage * invoicesPerPage, totalInvoices)} sur {totalInvoices} factures
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      } />
    </Routes>
  );
};

export default InvoicingPage;